import type { AppContext } from '@application/context/AppContext';
import type { LicenceRuntime } from '@infra/licence/LicenceRuntime';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { resolveEntitlementsEndpoints } from '@infra/licence/activationConstants';
import { resilientFetch } from '@infra/network/resilientFetch';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { logger } from '@infra/telemetry/Logger';
import { verifyServerToken } from '@infra/licence/ServerTokenVerifier';
import { LicenceStatusService } from '@infra/licence/LicenceStatusService';
import {
  notifyLicenceUpdated,
  syncLicenceEntitlements,
} from '@application/licence/LicenceEntitlementService';

export interface ServerEntitlements {
  /** JWT ES256 signé (typ 'entitlements') : seule source de droits acceptée. */
  entitlements_jwt?: string;
  /** Champs en clair du corps : INFORMATIFS, jamais appliqués (non signés). */
  features?: string[];
  role_views?: Record<string, string[]>;
  tier?: string;
  data_mode?: 'LOCAL_OFFLINE' | 'REMOTE_MULTI_STATION';
  deployment_mode?: 'standalone' | 'hybride' | 'cloud';
  server_time?: string;
}

const DATA_MODES = ['LOCAL_OFFLINE', 'REMOTE_MULTI_STATION'] as const;
const DEPLOYMENT_MODES = ['standalone', 'hybride', 'cloud'] as const;

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((v) => typeof v === 'string');
}

function isRoleViews(value: unknown): value is Record<string, string[]> {
  return (
    typeof value === 'object' &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every(isStringArray)
  );
}

/**
 * Interroge le serveur de licence pour les modules et profils ERP courants.
 * Ne renouvelle pas le certificat JWT — adapté à la synchro métier / connexion.
 */
export async function fetchAndApplyEntitlements(
  ctx: AppContext,
  runtime: LicenceRuntime,
): Promise<boolean> {
  if (!NetworkStatusService.isBrowserOnline()) {
    logger.info({ event: 'entitlement_fetch_skipped_offline' });
    return false;
  }

  const jwt = await SecureLicenceStore.getActivationCertificate();
  const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
  if (!jwt || !fingerprint) return false;

  for (const endpoint of resolveEntitlementsEndpoints()) {
    try {
      const res = await resilientFetch(
        endpoint,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            certificate: jwt,
            device_fingerprint: fingerprint,
          }),
        },
        { timeoutMs: 10_000, retries: 0 },
      );
      if (!res.ok) {
        if (res.status === 403) {
          await LicenceStatusService.handleServerRefusal(
            await readRefusalCode(res),
            runtime.getState().claims?.tenant_id,
          );
        }
        continue;
      }

      const data = (await res.json()) as ServerEntitlements;
      // Les droits ne viennent que du JWT vérifié ; le corps en clair est ignoré.
      const verified = await verifyServerToken(data.entitlements_jwt, 'entitlements', {
        deviceFingerprint: fingerprint,
      });
      if (!verified.ok) {
        logger.warn({ event: 'entitlements_rejected', reason: verified.reason });
        continue;
      }
      const claims = verified.payload;
      const features = claims.features;
      if (!isStringArray(features)) {
        logger.warn({ event: 'entitlements_rejected', reason: 'features_invalides' });
        continue;
      }
      const tier = typeof claims.tier === 'string' ? claims.tier : undefined;
      const dataMode = DATA_MODES.find((m) => m === claims.data_mode);
      const deploymentMode = DEPLOYMENT_MODES.find((m) => m === claims.deployment_mode);
      const roleViews = isRoleViews(claims.role_views)
        ? claims.role_views
        : (runtime.getState().claims?.role_views ?? {});

      runtime.applyEntitlements(features, roleViews, tier, dataMode, deploymentMode);
      await syncLicenceEntitlements(ctx, runtime);
      notifyLicenceUpdated();
      logger.info({ event: 'entitlements_fetched', modules: features.length });
      return true;
    } catch {
      // repli endpoint suivant
    }
  }
  return false;
}

async function readRefusalCode(res: Response): Promise<string | undefined> {
  try {
    return LicenceStatusService.extractErrorCode(await res.json());
  } catch {
    return undefined;
  }
}
